import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
// import { saveAs } from 'file-saver';
import { Grid } from '@uhg-abyss/web/ui/Grid';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { useParams } from '@uhg-abyss/web/tools/reactRouterTools/reactRouterDom';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { globalCss as global } from '@uhg-abyss/web/tools/styled';
import { config } from '@uhg-abyss/web/tools/config';
import { ServiceSandbox } from '@src/common/ServiceSandbox';
import { animateScroll } from 'react-scroll';
import { event } from '@uhg-abyss/web/tools/event';
import axios from 'axios';
import { LagoonContext } from '@src/AppContext';
import { PageHeading } from './PageHeading/PageHeading';
import { useDocumentation } from '@src/hooks/useDocumentation';
import { SideNav } from './SideNav';
import { APIDetails } from './APIDetails';
import { ErrorCodes } from './ErrorCodes';
import 'regenerator-runtime/runtime';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';

const INITIAL_CURRENT_API = {
  description: '',
  isDisabled: false,
  method: '',
  operationId: '',
  operationSlug: '',
  parameters: [],
  path: '',
  produces: [],
  responses: [],
  summary: '',
  tags: [],
};

const UpdatedSandboxStyle = global({
  '.abyss-table-cell': {
    wordBreak: 'unset !important',
  },
  '.abyss-box-root': {
    alignItems: 'flex-start',
  },
});

const StyledContent = (props) => {
  return (
    <Grid.Col
      css={{
        'abyss-grid-col': {
          paddingLeft: '0px !important',
          paddingRight: '0px !important',
        },
      }}
      {...props}
    />
  );
};

const getUpdatedProperty = (defData, key, required, updatedSchema) => {
  if (defData && Object.keys(defData).length > 0) {
    if (defData.properties) {
      return {
        ...updatedSchema,
        [key]: {
          type: 'object',
          properties: defData.properties,
          description: defData.description,
          required,
        },
      };
    }
    return {
      ...updatedSchema,
      [key]: {
        type: defData.type,
        description: defData.description,
        required,
      },
    };
  }
  return updatedSchema;
};

const isNewSwagger = (data) => {
  return Object.keys(data).includes('openapi');
};

// gets definition if there is $ref for body
const getDefinition = (data, newSwagger, ref, visited = new Map()) => {
  const definition = ref.split('/').pop();

  // Check for cyclic definitions
  if (visited.has(definition)) {
    return visited.get(definition); // Return the visited definition
  }

  // Placeholder for cyclic references
  visited.set(definition, {});

  const definitionBlock = newSwagger
    ? data.components.schemas[definition]
    : data.definitions[definition];
  const defProperties = definitionBlock?.properties;
  let updatedSchema = {};
  if (!defProperties) {
    visited.set(definition, definitionBlock);
    return definitionBlock;
  }
  Object.keys(defProperties).forEach((key) => {
    let required = false;
    if (definitionBlock.required?.includes(key)) {
      required = true;
    }
    if (defProperties[key]?.$ref) {
      const defData = getDefinition(
        data,
        newSwagger,
        defProperties[key].$ref,
        visited
      );
      updatedSchema = getUpdatedProperty(defData, key, required, updatedSchema);
    } else if (defProperties[key]?.type === 'array') {
      // eslint-disable-next-line no-use-before-define
      updatedSchema = getUpdatedArrayProperty(
        data,
        newSwagger,
        defProperties,
        key,
        required,
        updatedSchema,
        visited
      );
    } else if (defProperties[key]?.enum) {
      updatedSchema = {
        ...updatedSchema,
        [key]: {
          type: 'string',
          required,
          description: defProperties[key]?.description,
        },
      };
    } else if (defProperties[key]?.allOf?.length > 0) {
      const updatedDefData = {
        properties: {},
      };
      defProperties[key].allOf.forEach((item) => {
        if (item.$ref) {
          const defData = getDefinition(data, newSwagger, item.$ref, visited);
          if (defData?.properties) {
            Object.assign(updatedDefData.properties, defData.properties);
          }
        }
      });
      updatedSchema = getUpdatedProperty(
        updatedDefData,
        key,
        required,
        updatedSchema
      );
    } else {
      updatedSchema = {
        ...updatedSchema,
        [key]: {
          ...defProperties[key],
          required,
        },
      };
    }
  });
  let updatedDefinitions = {};
  if (newSwagger) {
    updatedDefinitions = {
      ...data.components.schemas[definition],
      properties: updatedSchema,
    };
  } else {
    updatedDefinitions = {
      ...data.definitions[definition],
      properties: updatedSchema,
    };
  }

  // Update the visited definition with the actual definition
  visited.set(definition, updatedDefinitions);

  return updatedDefinitions;
};

const getUpdatedArrayProperty = (
  data,
  newSwagger,
  defProperties,
  key,
  required,
  updatedSchema,
  visited
) => {
  if (defProperties[key].items?.$ref) {
    const defData = getDefinition(
      data,
      newSwagger,
      defProperties[key].items.$ref,
      visited
    );
    if (defData && Object.keys(defData).length > 0) {
      return {
        ...updatedSchema,
        [key]: {
          type: 'array',
          items: {
            properties: defData.properties,
          },
          description: defData.description,
          required,
        },
      };
    }
    return {
      ...updatedSchema,
      [key]: {
        type: `array[${key}]`,
        required,
      },
    };
  }
  return {
    ...updatedSchema,
    [key]: {
      type: `array[${defProperties[key]?.items?.type}]`,
      required,
    },
  };
};

const getParameters = (parameters, type) => {
  const filteredParameters = (parameters || []).filter((param) => {
    return param.in === type;
  });

  const formattedParameters = filteredParameters.map((param) => {
    return {
      key: param.key,
      value: param.value,
    };
  });

  return formattedParameters;
};

const setupParams = (params) => {
  let paramObj = {};
  for (let i = 0; i < params.length; i++) {
    const element = params[i];
    if (element.value) {
      paramObj = {
        ...paramObj,
        [element.key]: element.value,
      };
    }
  }
  return paramObj;
};

export const Documentation = () => {
  const { service, slug } = useParams();
  const [currentApi, setCurrentApi] = useState(INITIAL_CURRENT_API);
  const dsUserInfo = useSelector((state) => {
    return state.GET_DS_USER_INFO.data;
  });
  const userInfo = useSelector((state) => {
    return state.GET_USER_INFO.data;
  });
  const [bodySchemaFields, setBodySchemaFields] = useState({});
  const lagoonContext = React.useContext(LagoonContext);
  const sandbox = lagoonContext?.getTable('sandbox');
  const sandboxDataList = sandbox?.getRow();

  const defaultSandboxParameters = {
    pathurl: '',
    method: '',
    isSuperConnector: false,
    onSend: () => {},
    responses: [
      {
        response: '200',
        description: 'OK',
        header: [],
        query: [],
        parameters: [],
        body: {},
      },
    ],
    parameters: [],
  };

  const [sandboxParameters, setSandboxParameters] = useState(
    defaultSandboxParameters
  );
  const { navigate } = useRouter();

  const getContentType = (requestBody) => {
    if (requestBody) {
      return Object.keys(requestBody.content)[0];
    }

    return null;
  };

  const getFormData = (returnVal) => {
    const bodyFormData = (returnVal.parameters || [])?.filter((param) => {
      return param.in === 'formData';
    });
    let formData = bodyFormData;
    if (bodyFormData) {
      formData = bodyFormData.map((param) => {
        return {
          key: param.name,
          description: param.description,
          value: param.enum ? param.enum[0] : '',
          type: param.type,
          required: param.required,
        };
      });
    }
    return formData;
  };

  const getV3FormData = (returnVal, parameters) => {
    const hasFormData = /post|put/i.test(returnVal.method);
    if (!hasFormData) {
      return null;
    }
    const contentType = getContentType(returnVal?.requestBody);
    if (contentType !== 'multipart/form-data') {
      return null;
    }
    const bodyParameter = returnVal.requestBody;
    const bodySchema = bodyParameter?.content[contentType].schema;
    const formData = [];
    if (bodySchema?.properties) {
      const bodyFormData = bodySchema?.properties || [];
      Object.keys(bodyFormData).forEach((key) => {
        const param = bodyFormData[key];
        const required = bodySchema?.required?.includes(key);

        // openapi 3 uses type string and format as binary for files
        const type =
          param.type === 'string' && param.format === 'binary'
            ? 'file'
            : param.type;
        const obj = {
          key,
          description: param.description,
          value: param.enum ? param.enum[0] : '',
          type,
          required,
        };
        formData.push(obj);

        parameters.push({ ...obj, in: 'formData' });
      });
    }

    return formData;
  };

  const getV3SwaggerBody = (data, returnVal, parameters) => {
    const hasBody = /post|put/i.test(returnVal.method);
    if (!hasBody) {
      return null;
    }
    const contentType = getContentType(returnVal?.requestBody);
    if (contentType === 'multipart/form-data') {
      return null;
    }
    let bodySchema;
    if (contentType) {
      parameters.push({
        key: 'Content-Type',
        description: 'content type of request body',
        value: contentType,
        type: 'string',
        in: 'header',
      });
    }

    const bodyParameter = returnVal.requestBody;
    const apiBody = bodyParameter?.content[contentType];
    bodySchema = apiBody?.schema;
    if (!bodySchema) {
      bodySchema = { example: null };
    }
    if (bodySchema?.$ref) {
      bodySchema = getDefinition(
        data,
        true,
        bodyParameter?.content[contentType].schema.$ref
      );
    }
    setBodySchemaFields(bodySchema);
    return apiBody?.example || bodySchema?.example || apiBody?.examples;
  };

  const getBody = (data, returnVal) => {
    const hasBody = /post|put/i.test(returnVal.method);
    if (!hasBody) {
      return null;
    }
    let bodySchema;
    const bodyParameter = (returnVal.parameters || []).find((param) => {
      return param.in === 'body';
    });
    bodySchema = bodyParameter?.schema;
    if (!bodySchema) {
      bodySchema = { example: null };
    }
    if (bodySchema?.$ref) {
      bodySchema = getDefinition(data, false, bodyParameter.schema.$ref);
    }
    setBodySchemaFields(bodySchema);
    return bodySchema.example;
  };

  const getParams = (returnVal) => {
    const parameterValue = returnVal?.parameters?.map((param) => {
      return {
        in: param.in,
        key: param.name,
        description: param.description,
        value: param.enum ? param.enum[0] : '',
        type: param.type,
        required: param.required,
      };
    });
    return parameterValue;
  };

  const getV3Params = (returnVal, data) => {
    const parameterValue = returnVal?.parameters?.map((param) => {
      if (param?.$ref) {
        const definition = param?.$ref.split('/').pop();
        const parameter = data.components.parameters[definition];
        return {
          in: parameter.in,
          key: parameter.name,
          description: parameter.description,
          value:
            parameter.schema?.example ??
            (parameter.schema?.enum ? parameter.schema.enum[0] : ''),
          type: parameter.schema?.type,
          required: parameter.required,
        };
      }
      return {
        in: param.in,
        key: param.name,
        description: param.description,
        value:
          param.schema?.example ??
          (param.schema?.enum ? param.schema.enum[0] : ''),
        type: param.schema?.type,
        required: param.required,
      };
    });
    return parameterValue;
  };
  const getV3PathUrl = (data, returnVal) => {
    const { path } = returnVal;
    const { servers } = data;
    if (config('APP_ENV') === 'local') {
      const serverUrl = new URL(servers[0].url);
      const serverPath = serverUrl.pathname;

      return `${config('SANDBOX_API_URL')}${serverPath}${path}`;
    }
    return `${servers[0].url}${path}`;
  };

  const handleSendSuccess = (summary, method) => {
    const interactionName = `${service} ${summary}`;
    const interactionValue = 'send';
    const interactionContext = method;
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  const getAllParameters = (newSwagger, returnVal, data) => {
    if (newSwagger) {
      return getV3Params(returnVal, data) || [];
    }
    return getParams(returnVal) || [];
  };

  const getResponseContentType = (response, produces) => {
    if (response) {
      return Object.keys(response)[0];
    }
    // condition will be removed if all swaggers upgraded to v3.
    if (produces && produces.length > 0) {
      return produces[0];
    }
    return null;
  };

  // returns an object with API info for the API defined by the service and slug params
  const getCurrentApi = (data, slugParm) => {
    let returnVal;
    data.pathsByTag.forEach((listByTag) => {
      listByTag.paths.forEach((path) => {
        if (path.operationSlug.toLowerCase() === slugParm.toLowerCase()) {
          returnVal = path;
        }
      });
    });

    if (returnVal) {
      setCurrentApi(returnVal);
      if (slugParm) {
        event('DOCUMENTATION_PAGE_LOAD', {
          service,
          slug: slugParm,
          dsUserInfo,
          userInfo,
        });
      }

      const newSwagger = isNewSwagger(data);
      const parameters = getAllParameters(newSwagger, returnVal, data);

      if (
        !parameters.some((e) => {
          return e.key === 'env';
        })
      ) {
        parameters.push({
          key: 'env',
          description: 'env',
          value: 'sandbox',
          type: 'string',
          required: false,
          in: 'header',
        });
      }

      const { method } = returnVal;
      const onSend = async (url, options, callback) => {
        const idToCheck = slugParm;
        const apiMatchedObject = sandboxDataList?.find((obj) => {
          return obj.id === idToCheck;
        });
        if (apiMatchedObject) {
          try {
            const response = await axios.get(apiMatchedObject.url, {
              responseType: 'blob',
            });
            const finalResponse = {
              data: response.data,
              headers: response.headers,
            };
            callback(finalResponse);
          } catch {
            callback('Sandbox response for this API is not applicable.');
          }
        } else {
          const successResponseObj = returnVal.responses.find((response) => {
            return response.status === '200' || response.status === '202';
          });
          const responseContentType = getResponseContentType(
            successResponseObj?.content,
            returnVal?.produces
          );

          // condition will be removed if all swaggers upgraded to v3.
          if (newSwagger) {
            const response200 = successResponseObj?.content;
            if (responseContentType === 'application/octet-stream') {
              callback({
                data: response200[responseContentType]?.example,
                headers: { 'content-type': responseContentType },
              });
            } else if (data.service === 'superconnector') {
              callback({ data: response200[responseContentType]?.examples });
            } else {
              callback({ data: response200[responseContentType]?.example });
            }
          } else if (responseContentType === 'application/octet-stream') {
            callback({
              data: successResponseObj?.examples[responseContentType],
              headers: { 'content-type': responseContentType },
            });
          } else {
            callback({
              data: successResponseObj?.examples[responseContentType],
            });
          }

          handleSendSuccess(returnVal.summary, returnVal.method);
        }
      };

      let body;
      let formData;
      let pathurl;
      if (newSwagger) {
        pathurl = getV3PathUrl(data, returnVal);
        body = getV3SwaggerBody(data, returnVal, parameters);
        formData = getV3FormData(returnVal, parameters);
      } else {
        const host = config('SANDBOX_API_URL');
        const { basePath } = data;
        const { path } = returnVal;
        pathurl = `${host}${basePath}${path}`;
        body = getBody(data, returnVal);
        formData = getFormData(returnVal);
      }

      const queryParameters = getParameters(parameters, 'query');
      const headerParameters = getParameters(parameters, 'header');
      const responses = [];
      if (data.service === 'superconnector') {
        if (body && Object.keys(body).length > 0) {
          Object.keys(body).forEach((key) => {
            responses.push({
              response: key,
              operationName: key,
              parameters: {
                query: setupParams(queryParameters),
                header: setupParams(headerParameters),
                body: body[key]?.value,
                formData,
              },
            });
          });
        }
      } else {
        responses.push({
          response: '200',
          description: 'OK',
          parameters: {
            query: setupParams(queryParameters),
            header: setupParams(headerParameters),
            body,
            formData,
          },
        });
      }

      const sandboxParameterObject = {
        pathurl,
        method,
        onSend,
        responses,
        parameters,
        isSuperConnector: data.service === 'superconnector',
      };

      setSandboxParameters(sandboxParameterObject);
    }
  };

  // navigating first time to page doesn't know which path
  // so default to the first one.  If refreshing page there is
  // slug so use that.
  const handleCompleted = React.useCallback(
    (data) => {
      if (data.pathsByTag && data.pathsByTag[0]) {
        const returnVal = { ...data.pathsByTag[0].paths[0] };
        navigate(
          `/documentation/${data.service}/${returnVal.operationSlug || slug}`
        );
        getCurrentApi(data, returnVal.operationSlug || slug);
      }
    },
    [slug, service] // Add dependencies here
  );

  const [documentation, getDocumentation] = useDocumentation({
    onCompleted: handleCompleted,
    onError: () => {
      setCurrentApi(INITIAL_CURRENT_API);
    },
  });
  const { data } = documentation;
  useEffect(() => {
    if (service) {
      // Reset state to initial values
      setCurrentApi(INITIAL_CURRENT_API);
      setSandboxParameters(defaultSandboxParameters);
      setBodySchemaFields({});

      // Fetch new documentation data
      getDocumentation({ userInfo, service });
    }
  }, [service]);

  useEffect(() => {
    if (data && slug) {
      getCurrentApi(data, slug);
      animateScroll.scrollToTop();
    }
  }, [data, slug]);

  const handleTabChange = (tab) => {
    const interactionName = `${service} ${currentApi.method} ${currentApi.summary}`;
    const interactionValueList = ['api sandbox', 'api details', 'error codes'];
    const interactionValue = interactionValueList[tab];
    const interactionContext = 'tab clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  return (
    <LoadingOverlay
      label={service}
      isLoading={documentation.loading}
      isError={documentation.error}
    >
      <Grid>
        <StyledContent span={3}>
          <SideNav>
            <SideNav.Link
              service={service}
              data={data}
              currentTags={currentApi.tags}
            />
          </SideNav>
        </StyledContent>
        <StyledContent span={9}>
          <div>
            <PageHeading
              info={data?.info}
              currentApi={currentApi}
              service={service}
            />
            <Tabs
              onTabChange={handleTabChange}
              css={{
                '.abyss-tabs-tab-content': { fontSize: '15px' },
              }}
            >
              <Tabs.Tab label="API Sandbox">
                <UpdatedSandboxStyle />
                <ServiceSandbox
                  url={sandboxParameters.pathurl}
                  method={sandboxParameters.method.toLowerCase()}
                  parameters={sandboxParameters.parameters}
                  responses={sandboxParameters.responses}
                  isSuperConnector={sandboxParameters.isSuperConnector}
                  currentApi={
                    bodySchemaFields?.properties
                      ? bodySchemaFields.properties
                      : []
                  }
                  onSend={sandboxParameters.onSend}
                  onSuccess={() => {
                    return handleSendSuccess(
                      currentApi.summary,
                      currentApi.method
                    );
                  }}
                  css={{
                    'abyss-code-highlighter-copy': {
                      position: 'absolute !important',
                    },
                    '.abyss-button-root': {
                      width: '150px',
                      height: '38px',
                    },
                  }}
                />
              </Tabs.Tab>
              <Tabs.Tab label="API Details">
                <APIDetails currentApi={currentApi} />
              </Tabs.Tab>
              <Tabs.Tab label="Error Codes">
                <ErrorCodes currentApi={currentApi} />
              </Tabs.Tab>
            </Tabs>
          </div>
        </StyledContent>
      </Grid>
    </LoadingOverlay>
  );
};
